import { useState } from "react";
import { User } from "@/hooks/useUsersApi";
import OrdersTable from "@/components/orders/OrdersTable";
import { Card } from "@/components/atoms/card";
import { UserAvatar } from "@/components/atoms/avatar";
import { Pencil, ArchiveX, ArchiveRestore } from "lucide-react";
import UserDeactivationModal from "@/components/modals/UserDeactivationModal";

interface UsersTableProps {
  users: User[];
  searchQuery: string;
  selectedBrand: (string | number)[];
  selectedRole: string;
  onUserUpdated?: (userId: number, isReactivating: boolean) => void;
  onEditUser?: (user: User) => void;
  brands: any[]; // Add brands as prop
}

export default function UsersTable({
  users,
  searchQuery,
  selectedBrand,
  selectedRole,
  onUserUpdated,
  onEditUser,
  brands,
}: UsersTableProps) {
  const [isDeactivationModalOpen, setIsDeactivationModalOpen] = useState(false);
  const [selectedUserForDeactivation, setSelectedUserForDeactivation] =
    useState<User | null>(null);
  const [modalMode, setModalMode] = useState<"deactivate" | "reactivate">(
    "deactivate",
  );
  const [deactivatedUsers, setDeactivatedUsers] = useState<Set<number>>(
    new Set(),
  );
  const [reactivatedUsers, setReactivatedUsers] = useState<Set<number>>(
    new Set(),
  );

  const capitalizeRole = (role: string | undefined) => {
    if (!role) return "Sin rol";
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  const getUserBrandNames = (user: User) => {
    if (user.brands && Array.isArray(user.brands)) {
      return user.brands.map((brand: any) => brand.name);
    }

    if (user.brand_ids && Array.isArray(user.brand_ids)) {
      if (brands.length === 0) return [];
      return user.brand_ids
        .map((brandId) => brands.find((b) => b.id === brandId)?.name)
        .filter(Boolean) as string[];
    }

    return [];
  };

  // Nueva función para renderizar las etiquetas de marcas con límite de 3
  const renderBrandTags = (user: User, isMobile: boolean = false) => {
    const brandNames = getUserBrandNames(user);
    const displayBrands = brandNames.slice(0, 3);
    const remainingCount = brandNames.length - 3;

    return (
      <div className="flex flex-wrap gap-1">
        {displayBrands.map((brandName: string, index: number) => (
          <span
            key={`${user.id}-brand-${brandName}-${index}`}
            className={`inline-flex px-2 py-1 font-medium rounded-full ${
              isMobile
                ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-gray-50 text-gray-700 rounded-md border border-gray-200"
                : "text-xs bg-gray-100 text-gray-800"
            }`}
          >
            {brandName}
          </span>
        ))}
        {remainingCount > 0 && (
          <span
            className={`inline-flex px-2 py-1 font-medium rounded-full ${
              isMobile
                ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-gray-50 text-gray-700 rounded-md border border-gray-200"
                : "text-xs bg-gray-100 text-gray-800"
            }`}
          >
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  // Función para determinar si un usuario está archivado
  const getUserStatus = (user: User) => {
    const isLocallyDeactivated = deactivatedUsers.has(user.id);
    const isLocallyReactivated = reactivatedUsers.has(user.id);
    const isArchived =
      (user.active === false && !isLocallyReactivated) || isLocallyDeactivated;

    return isArchived ? "Archived" : "Active";
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !searchQuery ||
      user.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());

    let matchesBrand = true;
    if (selectedBrand.length > 0) {
      const selectedBrandIds = selectedBrand.map((id) =>
        typeof id === "number" ? id : parseInt(id.toString()),
      );

      if (user.brand_ids && Array.isArray(user.brand_ids)) {
        matchesBrand = selectedBrandIds.some((selectedId) =>
          user.brand_ids!.some((userBrandId: any) => {
            const normalizedUserBrandId =
              typeof userBrandId === "number"
                ? userBrandId
                : parseInt(userBrandId);
            return normalizedUserBrandId === selectedId;
          }),
        );
      } else if (user.brands && Array.isArray(user.brands)) {
        matchesBrand = selectedBrandIds.some((selectedId) =>
          user.brands!.some((brand: any) => {
            const brandId = typeof brand === "object" ? brand.id : brand;
            const normalizedBrandId =
              typeof brandId === "number" ? brandId : parseInt(brandId);
            return normalizedBrandId === selectedId;
          }),
        );
      } else {
        matchesBrand = false;
      }
    }

    const userRole = user.role?.toLowerCase();
    const matchesRole =
      selectedRole === "all" || userRole === selectedRole.toLowerCase();
    const shouldShow = userRole !== "driver";

    return matchesSearch && matchesBrand && matchesRole && shouldShow;
  });

  const columns = [
    { header: "Nombre", key: "name" },
    { header: "Marca", key: "brand" },
    { header: "Rol", key: "role" },
    { header: "Acciones", key: "actions" },
  ];

  const handleDeactivateUser = (user: User) => {
    setSelectedUserForDeactivation(user);
    setModalMode("deactivate");
    setIsDeactivationModalOpen(true);
  };

  const handleReactivateUser = (user: User) => {
    setSelectedUserForDeactivation(user);
    setModalMode("reactivate");
    setIsDeactivationModalOpen(true);
  };

  const handleUserActionSuccess = (userId: number) => {
    if (modalMode === "deactivate") {
      setDeactivatedUsers((prev) => new Set(prev).add(userId));
      setReactivatedUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      onUserUpdated?.(userId, false);
    } else {
      setReactivatedUsers((prev) => new Set(prev).add(userId));
      setDeactivatedUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      onUserUpdated?.(userId, true);
    }
  };

  return (
    <>
      <div className="mt-6">
        <div className="hidden lg:block">
          <OrdersTable
            columns={columns}
            emptyMessage="No hay usuarios disponibles"
          >
            {filteredUsers.map((user) => {
              const isLocallyDeactivated = deactivatedUsers.has(user.id);
              const isLocallyReactivated = reactivatedUsers.has(user.id);
              const isArchived =
                (user.active === false && !isLocallyReactivated) ||
                isLocallyDeactivated;
              const userStatus = getUserStatus(user);

              return (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <UserAvatar user={user} size="md" />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-gray-500 hidden xl:block">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {renderBrandTags(user, false)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-2">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.role?.toLowerCase() === "admin"
                            ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-green-100 text-green-800"
                            : user.role?.toLowerCase() === "manager"
                              ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-blue-100 text-blue-800"
                              : "sm:text-sm sm:px-3 sm:py-1 text-xs bg-gray-100 text-gray-800"
                        }`}
                      >
                        {capitalizeRole(
                          user.role === "manager" ? "encargado" : user.role,
                        )}
                      </span>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          userStatus === "Active"
                            ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-green-100 text-green-800"
                            : "sm:text-sm sm:px-3 sm:py-1 text-xs bg-red-100 text-red-800"
                        }`}
                      >
                        {userStatus}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <button
                        className="text-gray-400 hover:text-blue-600 transition-colors p-2 rounded hover:bg-blue-50"
                        onClick={() => onEditUser?.(user)}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        className={`text-gray-400 transition-colors p-2 rounded ${
                          isArchived
                            ? "hover:text-[#10B978] hover:bg-green-50"
                            : "hover:text-red-600 hover:bg-red-50"
                        }`}
                        onClick={() =>
                          isArchived
                            ? handleReactivateUser(user)
                            : handleDeactivateUser(user)
                        }
                      >
                        {isArchived ? (
                          <ArchiveRestore className="w-5 h-5" />
                        ) : (
                          <ArchiveX className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </OrdersTable>
        </div>

        <div className="block lg:hidden">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay usuarios disponibles
            </div>
          ) : (
            <div className="space-y-4">
              {filteredUsers.map((user) => {
                const isLocallyDeactivated = deactivatedUsers.has(user.id);
                const isLocallyReactivated = reactivatedUsers.has(user.id);
                const isArchived =
                  (user.active === false && !isLocallyReactivated) ||
                  isLocallyDeactivated;
                const userStatus = getUserStatus(user);

                return (
                  <Card
                    key={user.id}
                    className="bg-white rounded-lg shadow border p-3 sm:p-4 flex flex-row gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <UserAvatar user={user} size="lg" />
                        <div className="flex-1">
                          <div className="text-base font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-500 hidden sm:block">
                            {user.email}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 text-xs">
                        {renderBrandTags(user, true)}
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${
                            user.role?.toLowerCase() === "admin"
                              ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-green-100 text-green-800"
                              : user.role?.toLowerCase() === "manager"
                                ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-blue-100 text-blue-800"
                                : "sm:text-sm sm:px-3 sm:py-1 text-xs bg-gray-100 text-gray-800"
                          }`}
                        >
                          {capitalizeRole(
                            user.role === "manager" ? "encargado" : user.role,
                          )}
                        </span>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-md ${
                            userStatus === "Active"
                              ? "sm:text-sm sm:px-3 sm:py-1 text-xs bg-green-100 text-green-800"
                              : "sm:text-sm sm:px-3 sm:py-1 text-xs bg-red-100 text-red-800"
                          }`}
                        >
                          {userStatus}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 justify-end">
                        <button
                          className="text-gray-400 hover:text-blue-600 transition-colors p-2 rounded hover:bg-blue-50"
                          onClick={() => onEditUser?.(user)}
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          className={`text-gray-400 transition-colors p-2 rounded ${
                            isArchived
                              ? "hover:text-[#10B978] hover:bg-green-50"
                              : "hover:text-red-600 hover:bg-red-50"
                          }`}
                          onClick={() =>
                            isArchived
                              ? handleReactivateUser(user)
                              : handleDeactivateUser(user)
                          }
                        >
                          {isArchived ? (
                            <ArchiveRestore className="w-5 h-5" />
                          ) : (
                            <ArchiveX className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <UserDeactivationModal
        isOpen={isDeactivationModalOpen}
        onClose={() => setIsDeactivationModalOpen(false)}
        user={selectedUserForDeactivation}
        onDeactivationSuccess={handleUserActionSuccess}
        mode={modalMode}
      />
    </>
  );
}
